import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Copy, Download, Image as ImageIcon, Loader2, Palette, Pipette, Plus, Share2, Sparkles, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandingGuidePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const forClient = params.get("client_id");
  const q = forClient ? { params: { client_id: forClient } } : {};
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [palette, setPalette] = useState(null);
  const [colors, setColors] = useState([]);
  const [contact, setContact] = useState({});
  const [assets, setAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ai, setAi] = useState(null);
  const [picking, setPicking] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [hex, setHex] = useState("#2F5D50");
  const [logoColors, setLogoColors] = useState([]);
  const [readingLogo, setReadingLogo] = useState(false);
  const [social, setSocial] = useState(null);
  const [socialBusy, setSocialBusy] = useState(false);
  const fileRef = useRef(null);
  const hasEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

  useEffect(() => {
    api.get("/branding/me", q).then(({ data: d }) => {
      setData(d);
      if (d.access) {
        const g = d.guide || {};
        setAnswers(g.answers || {});
        setPalette(g.palette || null);
        setColors(g.colors || []);
        setContact(g.contact || {});
        setAssets(g.assets || []);
        setAi(g.ai || null);
        setSocial(g.social || null);
        setShareLink(d.share_link || null);
        setIdx(Math.min(g.current_step || 0, (d.steps || []).length - 1));
      }
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const steps = data?.steps || [];
  const step = steps[idx];
  const total = steps.length;
  const answeredCount = useMemo(
    () => steps.filter((s) => {
      if (s.type === "upload") return assets.some((a) => a.kind === s.asset_kind);
      if (s.type === "palette") return colors.length > 0;
      if (s.type === "contact") return Object.values(contact).some(Boolean);
      const v = answers[s.key];
      return Array.isArray(v) ? v.length > 0 : !!v;
    }).length,
    [steps, answers, assets, colors, contact],
  );

  const persist = async (extra = {}) => {
    setSaving(true);
    try {
      await api.put("/branding/me", { answers, palette, colors, contact, current_step: idx, ...extra }, q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const toggleColor = (c) => {
    const max = steps[idx]?.max || 5;
    const on = colors.includes(c);
    if (!on && colors.length >= max) { toast.error(`Maximal ${max} Farben`); return; }
    const next = on ? colors.filter((x) => x !== c) : [...colors, c];
    setColors(next);
    persist({ colors: next });
  };

  const addCustomColor = () => {
    const v = hex.trim().toUpperCase();
    if (!/^#[0-9A-F]{6}$/.test(v)) { toast.error("Bitte einen Farbcode wie #2F5D50 eingeben"); return; }
    if (colors.includes(v)) { toast.info("Farbe ist bereits dabei"); return; }
    const max = steps[idx]?.max || 5;
    if (colors.length >= max) { toast.error(`Maximal ${max} Farben – entferne zuerst eine`); return; }
    const next = [...colors, v];
    setColors(next);
    persist({ colors: next });
    toast.success(`${v} übernommen`);
  };

  const readLogoColors = async () => {
    setReadingLogo(true);
    try {
      const { data: res } = await api.post("/branding/me/logo-colors", {}, { timeout: 120000, ...q });
      setLogoColors(res.colors || []);
      toast.success(res.source === "logo" ? "Farben aus deinem Logo gelesen" : "Farben aus deinen Bildern gelesen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setReadingLogo(false); }
  };

  const makeSocial = async () => {
    setSocialBusy(true);
    try {
      const { data: res } = await api.post("/branding/me/social", {}, { timeout: 240000, ...q });
      setSocial(res.social);
      toast.success("Deine Social-Media-Texte sind fertig");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSocialBusy(false); }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert`);
    } catch { toast.error("Kopieren hat nicht geklappt – bitte den Text markieren"); }
  };

  const pickFromScreen = async () => {    try {
      const { sRGBHex } = await new window.EyeDropper().open();
      setHex(sRGBHex.toUpperCase());
    } catch { /* abgebrochen */ }
  };

  const go = async (dir) => {    await persist({ current_step: Math.max(0, Math.min(total - 1, idx + dir)) });
    setIdx((i) => Math.max(0, Math.min(total - 1, i + dir)));
  };

  const upload = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      const { data: asset } = await api.post("/branding/me/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000, ...q,
      });
      setAssets((p) => [...p, asset]);
      toast.success("Bild hinzugefügt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const pickGallery = async (item, kind) => {
    setPicking(item.id);
    try {
      const { data: asset } = await api.post("/branding/me/gallery-pick", { kind, image_id: item.id }, { timeout: 120000, ...q });
      setAssets((p) => [...p, asset]);
      toast.success("Beispielbild übernommen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setPicking(null); }
  };

  const toggleShare = async () => {
    try {
      const { data: res } = await api.post("/branding/me/share", { enabled: !shareLink }, q);
      setShareLink(res.link || null);
      if (res.link) {
        try { await navigator.clipboard.writeText(res.link); } catch { /* egal */ }
        toast.success("Online-Guide ist freigegeben – Link kopiert", { duration: 10000 });
      } else {
        toast.success("Der Link ist jetzt abgeschaltet");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const removeAsset = async (id) => {
    try {
      await api.delete(`/branding/me/asset/${id}`, q);
      setAssets((p) => p.filter((a) => a.asset_id !== id));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await persist();
      const { data: res } = await api.post("/branding/me/generate", {}, { timeout: 240000, ...q });
      setAi(res.ai);
      toast.success("Dein Marken-Guide ist fertig");
      setIdx(total); // Abschlussseite
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const download = async () => {
    try {
      const r = await api.get("/branding/me/pdf", { responseType: "blob", timeout: 240000, ...q });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!data) return <Shell><p className="text-sm text-slate-500">Lädt…</p></Shell>;
  if (!data.access) {
    return (
      <Shell>
        <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Branding-Dashboard</h1>
        <p className="text-sm text-slate-600 mt-3" data-testid="branding-no-access">
          {forClient
            ? "Für diesen Teilnehmer ist das Branding-Dashboard noch nicht freigeschaltet – in der Akte unter „Unterlagen“ kannst du es mit einem Klick freischalten."
            : "Für dich ist der Marken-Guide noch nicht freigeschaltet. Sprich deinen Coach an – er kann dir den Zugang in deiner Teilnehmerakte freigeben."}
        </p>
      </Shell>
    );
  }

  // ---------- Abschlussseite ----------
  if (idx >= total) {
    return (
      <Shell>
        <div className="text-center py-6" data-testid="branding-finish">
          <Sparkles size={38} strokeWidth={1} className="mx-auto text-slate-900 mb-4" />
          <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900">Dein Marken-Guide steht</h1>
          {ai?.tagline && <p className="text-base text-slate-700 italic mt-3">„{ai.tagline}“</p>}
          <p className="text-sm text-slate-600 mt-3 max-w-md mx-auto">
            {ai?.positioning || "Lade dir den Guide als PDF herunter – er liegt außerdem in deiner Teilnehmerakte."}
          </p>
          <div className="flex gap-3 justify-center mt-8 flex-wrap">
            <button onClick={download} data-testid="branding-download"
              className="px-5 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2">
              <Download size={14} strokeWidth={1.5} /> Marken-Guide als PDF
            </button>
            <button onClick={toggleShare} data-testid="branding-share"
              className="px-5 py-3 border border-slate-900 text-sm font-medium hover:bg-slate-100 flex items-center gap-2">
              <Share2 size={14} strokeWidth={1.5} /> {shareLink ? "Teilen beenden" : "Als Online-Guide teilen"}
            </button>
            <button onClick={() => setIdx(0)} data-testid="branding-edit-again"
              className="px-5 py-3 border border-slate-300 text-sm font-medium hover:bg-slate-100">
              Antworten überarbeiten
            </button>
            <button onClick={() => navigate(forClient ? `/avgs/${forClient}` : "/mein-bereich")} className="px-5 py-3 text-sm text-slate-600 hover:text-slate-900">
              {forClient ? "Zurück zur Akte" : "Zurück zu meinem Bereich"}
            </button>
          </div>
          {shareLink && (
            <div className="mt-6 border border-slate-200 bg-slate-50 p-4 text-left max-w-xl mx-auto" data-testid="branding-share-link">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Dein öffentlicher Link</div>
              <a href={shareLink} target="_blank" rel="noreferrer" className="text-sm text-slate-900 underline break-all">{shareLink}</a>
              <p className="text-xs text-slate-500 mt-2">
                Jeder mit diesem Link sieht deinen Guide online – ideal für Bewerbungen oder Instagram. Du kannst ihn jederzeit wieder abschalten.
              </p>
            </div>
          )}

          {ai && (
            <div className="text-left mt-12 border-t border-slate-200 pt-8" data-testid="branding-social-section">
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Selbstvermarktung</div>
                  <h2 className="font-heading text-lg md:text-lg font-light tracking-tight text-slate-900 mt-1">
                    Texte für LinkedIn & Instagram
                  </h2>
                  <p className="text-sm text-slate-600 mt-1 max-w-xl">
                    Aus deinen Antworten entstehen ein LinkedIn-Profil, eine Instagram-Bio und ein fertiger Vorstellungspost.
                  </p>
                </div>
                <button onClick={makeSocial} disabled={socialBusy} data-testid="branding-social-generate"
                  className="px-5 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50 flex items-center gap-2">
                  {socialBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
                  {socialBusy ? "Schreibt…" : social ? "Neu schreiben lassen" : "Texte erstellen"}
                </button>
              </div>

              {social && (
                <div className="grid gap-4 mt-6 sm:grid-cols-2" data-testid="branding-social-texts">
                  {[["LinkedIn · Profilslogan", social.linkedin_headline, "linkedin-headline"],
                    ["LinkedIn · Info-Text", social.linkedin_about, "linkedin-about"],
                    ["Instagram · Bio", social.instagram_bio, "instagram-bio"],
                    ["Vorstellungspost", social.intro_post, "intro-post"]].map(([label, text, id]) => (
                      text ? (
                        <div key={id} className="border border-slate-200 p-4" data-testid={`branding-social-${id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
                            <button onClick={() => copy(text, label)} data-testid={`branding-social-copy-${id}`}
                              className="text-slate-400 hover:text-slate-900 shrink-0" title="Text kopieren">
                              <Copy size={14} strokeWidth={1.5} />
                            </button>
                          </div>
                          <div className="text-sm text-slate-900 mt-2 whitespace-pre-line">{text}</div>
                        </div>
                      ) : null
                  ))}
                  {Array.isArray(social.hashtags) && social.hashtags.length > 0 && (
                    <div className="border border-slate-200 p-4 sm:col-span-2" data-testid="branding-social-hashtags">
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Hashtags</div>
                        <button onClick={() => copy(social.hashtags.map((t) => `#${String(t).replace(/^#/, "")}`).join(" "), "Hashtags")}
                          data-testid="branding-social-copy-hashtags"
                          className="text-slate-400 hover:text-slate-900 shrink-0" title="Hashtags kopieren">
                          <Copy size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {social.hashtags.map((t) => (
                          <span key={t} className="px-2.5 py-1 bg-slate-100 text-xs text-slate-700">#{String(t).replace(/^#/, "")}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {ai && (
            <div className="text-left mt-12 border-t border-slate-200 pt-8 grid gap-6 sm:grid-cols-2">
              <Block title="Vision">{ai.vision}</Block>
              <Block title="Markenpersönlichkeit">{ai.personality}</Block>
              <Block title="So klingst du">{ai.voice_description}</Block>
              <Block title="Schriften">{ai.typography ? `${ai.typography.heading} · ${ai.typography.body}` : ""}</Block>
            </div>
          )}
        </div>
      </Shell>
    );
  }

  const stepAssets = assets.filter((a) => a.kind === step.asset_kind);
  const value = answers[step.key];

  return (
    <Shell wide>
      {/* Kopf: Fortschritt */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            {data.section || "Selbstvermarktung"} · Schritt {idx + 1} von {total}
          </div>
          <div className="text-xs text-slate-500 mt-1">{answeredCount} von {total} ausgefüllt{saving ? " · speichert…" : ""}</div>
        </div>
        <button onClick={() => navigate(forClient ? `/avgs/${forClient}` : "/mein-bereich")} className="text-slate-400 hover:text-slate-700" aria-label="Schließen" data-testid="branding-close">
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>
      {forClient && (
        <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 mb-6" data-testid="branding-staff-mode">
          Begleit-Modus: Du siehst und bearbeitest die Präsentation von <b>{data.client_name}</b>. Alles, was du hier
          einträgst, steht auch im Portal des Teilnehmers.
        </div>
      )}
      <div className="h-[3px] bg-slate-200 mb-10">
        <motion.div className="h-full bg-slate-900" animate={{ width: `${((idx + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step.key} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.3 }} data-testid={`branding-step-${step.key}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{step.chapter}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">{step.title}</h1>
          <p className="text-base text-slate-700 mt-4 max-w-2xl">{step.question}</p>

          <div className="mt-8">
            {step.type === "text" && (
              <textarea
                data-testid="branding-input-text" rows={5} value={value || ""}
                placeholder={step.placeholder || "Schreib einfach drauflos…"}
                onChange={(e) => setAnswers((p) => ({ ...p, [step.key]: e.target.value }))}
                onBlur={() => persist()}
                className="w-full max-w-2xl border border-slate-300 p-4 text-base focus:outline-none focus:border-slate-900"
              />
            )}

            {step.type === "chips" && (
              <ChipInput value={Array.isArray(value) ? value : []} max={step.max} placeholder={step.placeholder}
                onChange={(v) => setAnswers((p) => ({ ...p, [step.key]: v }))} onBlur={() => persist()} />
            )}

            {step.type === "choice_text" && (
              <div className="space-y-3 max-w-2xl">
                {step.options.map((opt) => (
                  <button key={opt} data-testid={`branding-option-${opt.slice(0, 12)}`}
                    onClick={() => { setAnswers((p) => ({ ...p, [step.key]: opt })); persist({ answers: { ...answers, [step.key]: opt } }); }}
                    className={`w-full text-left px-5 py-4 border text-sm transition-colors ${value === opt ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 hover:bg-slate-50"}`}>
                    {opt}
                  </button>
                ))}
                <input value={typeof value === "string" && !step.options.includes(value) ? value : ""}
                  placeholder="…oder beschreibe es mit eigenen Worten"
                  data-testid="branding-option-custom"
                  onChange={(e) => setAnswers((p) => ({ ...p, [step.key]: e.target.value }))}
                  onBlur={() => persist()}
                  className="w-full border border-slate-300 px-5 py-4 text-sm focus:outline-none focus:border-slate-900" />
              </div>
            )}

            {step.type === "upload" && (
              <div>
                <div className="flex flex-wrap gap-4">
                  {stepAssets.map((a) => (
                    <div key={a.asset_id} className="relative group" data-testid={`branding-asset-${a.asset_id}`}>
                      <img src={`${API}/branding/asset/${a.asset_id}`} alt={a.filename}
                        className="w-40 h-40 object-cover border border-slate-200" />
                      <button onClick={() => removeAsset(a.asset_id)} aria-label="Bild entfernen"
                        data-testid={`branding-asset-remove-${a.asset_id}`}
                        className="absolute top-1 right-1 bg-white/90 border border-slate-200 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                  ))}
                  {stepAssets.length < (step.max || 5) && (
                    <>
                      <button onClick={() => fileRef.current?.click()} data-testid="branding-upload-btn"
                        className="w-40 h-40 border border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-slate-900 hover:text-slate-900">
                        <Plus size={18} strokeWidth={1.5} />
                        <span className="text-xs">Bild hochladen</span>
                      </button>
                      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                        onChange={(e) => { upload(e.target.files?.[0], step.asset_kind); e.target.value = ""; }} />
                    </>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5">
                  <ImageIcon size={12} strokeWidth={1.5} /> PNG, JPG oder WEBP · maximal {step.max} {step.max === 1 ? "Bild" : "Bilder"} · je bis 12 MB
                </p>
                {(data.gallery?.[step.asset_kind] || []).length > 0 && stepAssets.length < (step.max || 5) && (
                  <div className="mt-8 border-t border-slate-200 pt-6" data-testid="branding-gallery">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                      Keine eigenen Bilder? Wähle hier aus
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                      {data.gallery[step.asset_kind].map((item) => (
                        <button key={item.id} data-testid={`branding-gallery-${item.id}`}
                          disabled={picking === item.id}
                          onClick={() => pickGallery(item, step.asset_kind)}
                          className="group text-left border border-slate-200 hover:border-slate-900 transition-colors disabled:opacity-50">
                          <img src={item.url} alt={item.label} loading="lazy" className="w-full h-28 object-cover" />
                          <span className="block text-[11px] text-slate-600 px-2 py-2 group-hover:text-slate-900">
                            {picking === item.id ? "wird übernommen…" : item.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step.type === "palette" && (
              <div className="space-y-6">
                <div className="grid gap-3 sm:grid-cols-2">
                  {(data.palettes || []).map((p) => (
                    <button key={p.key} data-testid={`branding-palette-${p.key}`}
                      onClick={() => { setPalette(p.key); setColors(p.colors.slice(0, 4)); persist({ palette: p.key, colors: p.colors.slice(0, 4) }); }}
                      className={`text-left p-4 border transition-colors ${palette === p.key ? "border-slate-900" : "border-slate-200 hover:border-slate-400"}`}>
                      <div className="flex gap-1 mb-3">
                        {p.colors.map((c) => <span key={c} className="h-6 flex-1" style={{ background: c }} />)}
                      </div>
                      <div className="text-sm font-medium text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.mood}</div>
                    </button>
                  ))}
                </div>
                {palette && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                      Deine Farben anklicken (max. {step.max})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(data.palettes.find((p) => p.key === palette)?.colors || []).map((c) => {
                        const on = colors.includes(c);
                        return (
                          <button key={c} data-testid={`branding-color-${c.replace("#", "")}`}
                            onClick={() => toggleColor(c)}
                            className={`w-14 h-14 border-2 flex items-end justify-end p-1 ${on ? "border-slate-900" : "border-transparent"}`}
                            style={{ background: c }} aria-label={c}>
                            {on && <Check size={12} strokeWidth={3} className="text-white drop-shadow" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="border-t border-slate-200 pt-6">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
                    Eigene Farbe mischen
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    Farbe mit der Maus im Farbkreis wählen, mit der Pipette vom Bildschirm aufnehmen oder den Farbcode eintippen.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="relative w-14 h-14 rounded-full cursor-pointer border border-slate-300 flex items-center justify-center shrink-0"
                      style={{ background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)" }}
                      title="Farbkreis öffnen">
                      <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow">
                        <Plus size={16} strokeWidth={2.5} className="text-slate-900" />
                      </span>
                      <input type="color" value={hex} data-testid="branding-color-wheel"
                        onChange={(e) => setHex(e.target.value.toUpperCase())}
                        className="absolute inset-0 opacity-0 cursor-pointer" />
                    </label>

                    {hasEyeDropper && (
                      <button type="button" data-testid="branding-color-eyedropper" onClick={pickFromScreen}
                        title="Farbe vom Bildschirm aufnehmen"
                        className="w-14 h-14 rounded-full border border-slate-300 bg-white hover:border-slate-900 transition-colors flex items-center justify-center shrink-0">
                        <Pipette size={18} className="text-slate-900" />
                      </button>
                    )}

                    <div className="flex items-center border border-slate-300 h-14">
                      <span className="w-14 h-full shrink-0" style={{ background: hex }} />
                      <input value={hex} data-testid="branding-color-hex" maxLength={7} spellCheck={false}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          setHex(v.startsWith("#") ? v : `#${v}`);
                        }}
                        onKeyDown={(e) => { if (e.key === "Enter") addCustomColor(); }}
                        placeholder="#2F5D50"
                        className="w-28 px-3 text-sm font-mono uppercase focus:outline-none" />
                    </div>

                    <button type="button" data-testid="branding-color-add" onClick={addCustomColor}
                      className="h-14 px-5 bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors">
                      Farbe übernehmen
                    </button>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
                    Farben aus deinem Logo
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    Ich lese die Hauptfarben aus deinem hochgeladenen Logo aus – klicke die an, die du übernehmen willst.
                  </p>
                  <button type="button" onClick={readLogoColors} disabled={readingLogo} data-testid="branding-read-logo-colors"
                    className="px-4 py-2.5 border border-slate-900 text-sm font-medium hover:bg-slate-100 disabled:opacity-50 flex items-center gap-2">
                    {readingLogo ? <Loader2 size={14} className="animate-spin" /> : <Palette size={14} strokeWidth={1.5} />}
                    {readingLogo ? "Liest Farben…" : "Farben aus Logo lesen"}
                  </button>
                  {logoColors.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4" data-testid="branding-logo-colors">
                      {logoColors.map((c) => {
                        const on = colors.includes(c);
                        return (
                          <button key={c} type="button" data-testid={`branding-logo-color-${c.replace("#", "")}`}
                            onClick={() => toggleColor(c)} title={c}
                            className={`w-14 h-14 border-2 flex items-end justify-end p-1 ${on ? "border-slate-900" : "border-slate-200 hover:border-slate-500"}`}
                            style={{ background: c }}>
                            {on && <Check size={12} strokeWidth={3} className="text-white drop-shadow" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                    Deine Markenfarben ({colors.length}/{step.max || 5})
                  </div>
                  {colors.length === 0 ? (
                    <div className="text-xs text-slate-500">noch keine Farbe gewählt</div>
                  ) : (
                    <div className="flex flex-wrap gap-3" data-testid="branding-selected-colors">
                      {colors.map((c) => (
                        <div key={c} className="border border-slate-200">
                          <div className="w-20 h-16 relative group" style={{ background: c }}>
                            <button type="button" data-testid={`branding-color-remove-${c.replace("#", "")}`}
                              onClick={() => toggleColor(c)} aria-label={`${c} entfernen`}
                              className="absolute top-1 right-1 w-5 h-5 bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                              <X size={12} className="text-slate-900" />
                            </button>
                          </div>
                          <div className="px-2 py-1 text-[10px] font-mono text-slate-600">{c}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {step.type === "contact" && (
              <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
                {[["email", "E-Mail"], ["phone", "Telefon"], ["website", "Website"], ["social", "Social Media"]].map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="text-xs font-bold uppercase tracking-overline text-slate-500">{label}</span>
                    <input value={contact[k] || ""} data-testid={`branding-contact-${k}`}
                      onChange={(e) => setContact((p) => ({ ...p, [k]: e.target.value }))}
                      onBlur={() => persist()}
                      className="mt-1 w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900" />
                  </label>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-4 mt-14 pt-6 border-t border-slate-200">
        <button onClick={() => go(-1)} disabled={idx === 0} data-testid="branding-back"
          className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30 flex items-center gap-2">
          <ArrowLeft size={14} strokeWidth={1.5} /> Zurück
        </button>
        {idx < total - 1 ? (
          <button onClick={() => go(1)} data-testid="branding-next"
            className="px-6 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2">
            Weiter <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {ai && (
              <button onClick={() => setIdx(total)} data-testid="branding-open-result"
                className="px-5 py-3 border border-slate-900 text-sm font-medium hover:bg-slate-100 flex items-center gap-2">
                Zum fertigen Guide <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            )}
            <button onClick={generate} disabled={generating} data-testid="branding-generate"
              className="px-6 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50 flex items-center gap-2">
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
              {generating ? "Dein Guide entsteht…" : ai ? "Guide neu erstellen" : "Marken-Guide erstellen"}
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}

const Block = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">{title}</div>
    <p className="text-sm text-slate-700 leading-relaxed">{children || "—"}</p>
  </div>
);

const ChipInput = ({ value, max, placeholder, onChange, onBlur }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v || value.includes(v) || value.length >= (max || 10)) { setDraft(""); return; }
    onChange([...value, v]);
    setDraft("");
  };
  return (
    <div className="max-w-2xl">
      <div className="flex flex-wrap gap-2 mb-3">
        {value.map((w) => (
          <span key={w} data-testid={`branding-chip-${w}`} className="px-3 py-1.5 bg-slate-100 text-sm text-slate-800 flex items-center gap-2">
            {w}
            <button onClick={() => { onChange(value.filter((x) => x !== w)); }} aria-label={`${w} entfernen`}>
              <X size={11} strokeWidth={2} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={draft} placeholder={placeholder || "Wort eingeben"} data-testid="branding-chip-input"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          onBlur={() => { add(); onBlur?.(); }}
          className="flex-1 border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900" />
        <button onClick={add} data-testid="branding-chip-add" className="px-4 border border-slate-300 hover:bg-slate-100">
          <Plus size={14} strokeWidth={1.5} />
        </button>
      </div>
      <div className="text-xs text-slate-500 mt-2">{value.length} von {max} · mit Enter hinzufügen</div>
    </div>
  );
};

const Shell = ({ children, wide }) => (
  <div className="min-h-screen bg-white px-5 py-10 sm:px-10">
    <div className={`${wide ? "max-w-4xl" : "max-w-2xl"} mx-auto`}>{children}</div>
  </div>
);
